import { AnchorHTMLAttributes, forwardRef } from 'react';

import { useDesignSystem } from '../../../DesignSystemProvider/DesignSystemProvider';
import { AnchorTarget, ReferrerPolicy } from '../../../ts-types';
import styles from './LegacyLink.module.scss';

import { fromEntries } from '../../../utils/fromEntries';

export type LegacyLinkProps = {
  /** Indicates the element represents the current item within a set. */
  'aria-current'?: AnchorHTMLAttributes<HTMLAnchorElement>['aria-current'];
  /** Optional `aria-label` attribute to describe the `LegacyLink`. */
  'aria-label'?: string;
  /** The content of the `LegacyLink`. */
  children: React.ReactNode;
  /**
   * Optional prop for overriding styles.
   * Note that when this prop is passed, no styles will be
   * applied to `LegacyLink` except those defined by `className`;
   * `color` and `weight` props will have no effect.
   */
  className?: string;
  /**
   * The color of the `LegacyLink`. Note that this has no effect if
   * the `className` prop is passed. When a custom color is needed,
   * supply a custom `css` / `className` prop.
   * @default 'primary'
   */
  color?: 'primary' | 'secondary' | 'inherit' | null;
  /** A Component to pass to the LegacyLink if you'd like to render a different kind of link,
   * like a Router Link, for example.
   * Using the component prop overrides the `LinkComponent` passed down via `DesignSystemContext`.
   */
  component?: React.ElementType;
  /** Where the `LegacyLink` links to. */
  href?: string;
  /** Optional ID to identify the link. */
  id?: string;
  /** Optional `name` attribute allows consumers to create named anchor links. */
  name?: string;
  /**
   * Controls whether the `noreferrer` tag is added to the link.
   * Defaults to `true`.
   */
  noReferrer?: boolean;
  /** Optional `blur` handler. */
  onBlur?: React.ReactEventHandler;
  /** Optional `click` handler. */
  onClick?: React.MouseEventHandler;
  /** Optional `focus` handler. */
  onFocus?: React.ReactEventHandler;
  /** Optional `keyDown` handler. */
  onKeyDown?: React.KeyboardEventHandler;
  /** Optional `mousedown` handler. */
  onMouseDown?: React.MouseEventHandler;
  /** Optional `mouseenter` handler. */
  onMouseEnter?: React.MouseEventHandler;
  /** Optional `mouseleave` handler. */
  onMouseLeave?: React.MouseEventHandler;
  /** Optional `mouseup` handler. */
  onMouseUp?: React.MouseEventHandler;
  /**
   * The `rel` attribute of the `LegacyLink`, which is the relationship of the `LegacyLink` to its target.
   */
  rel?: string;
  /**
   * The `role` attribute of the `LegacyLink`, which specifies the function of the `LegacyLink`.
   */
  role?: string;
  /**
   * Inline styles for `LegacyLink`. Mostly used to pass custom CSS properties from `Button`.
   */
  style?: {};
  /**
   * Optional `tabindex` attribute to apply to the link.
   * Should only be used in rare circumstances!
   */
  tabIndex?: number;
  /**
   * The `target` attribute of the `LegacyLink`, which specifies how the `LegacyLink` should open.
   */
  target?: AnchorTarget;
  /**
   * The `referrerpolicy` attribute of the `LegacyLink`,
   * which specifies how much of the `referrer` to send when following the `LegacyLink`.
   */
  referrerPolicy?: ReferrerPolicy;
  /**
   * The `title` attribute of the `LegacyLink`, which provides additional text describing the `LegacyLink`.
   */
  title?: string;
  /**
   * The `font-weight` to use for `LegacyLink`.
   * Note that this has no effect if the `className` prop is passed.
   * @default 'inherit'
   */
  weight?: 'normal' | 'bold' | 'inherit' | null;
};

const DOMAIN_WHITELIST = ['zapier.com', 'zapier-staging.com', 'localhost'];
const isLinkExternal = (href?: string) => {
  if (!href || href.length === 0) {
    return false;
  }

  const matcher = /(^[?#/]|http)|^[^.]+(\/)/;
  let hrefWithProtocol = href;

  if (!matcher.test(href)) {
    hrefWithProtocol = `https://${href}`;
  }

  const baseUrl = 'https://zapier.com';
  let url;
  try {
    url = new URL(hrefWithProtocol, baseUrl);
  } catch (_) {
    return true;
  }
  const [topLevelDomain, secondLevelDomain] = url.host
    .split(':')[0]
    .split('.')
    .reverse();
  const domain = secondLevelDomain
    ? `${secondLevelDomain}.${topLevelDomain}`
    : topLevelDomain;
  return !DOMAIN_WHITELIST.includes(domain);
};

const buildRel = (
  rel: string | undefined | null,
  isExternal: boolean,
  noReferrer: boolean
) => {
  const externalLinkRel = [];

  if (isExternal) {
    externalLinkRel.push('noopener');
  }

  if (isExternal && noReferrer) {
    externalLinkRel.push('noreferrer');
  }

  const propsRel = (rel && rel.split(/\s+/)) || [];

  return [...new Set([...propsRel, ...externalLinkRel])].join(' ') || undefined;
};

/**
 * <span style="color: #a81227; font-weight: bold;">⚠️ This component is deprecated in favor of [Link](https://zinnia-storybook.zapier.com/?path=/docs/navigation-link--docs).</span>
 *
 * Use `LegacyLink` to render a link to another URL. If `LegacyLink` doesn't link to a different URL, consider using a `Button` instead.
 *
 * @deprecated Use `Link` instead. `LegacyLink` will be removed in a future version.
 */
export const LegacyLink = forwardRef(function LegacyLink(
  props: LegacyLinkProps,
  ref: React.Ref<HTMLAnchorElement>
) {
  const {
    color = 'primary',
    component: Component,
    noReferrer = true,
    weight = 'inherit',
    'aria-label': ariaLabel,
    'aria-current': ariaCurrent,
  } = props;
  const colorMaybeNull = color === 'inherit' ? null : color;
  const trimmedHref = props.href ? props.href.trim() : props.href;
  const { LinkComponent: ContextLink } = useDesignSystem();
  const LinkComponent = Component || ContextLink;

  const dataAttributes = fromEntries(
    Object.entries(props).filter(([key]) => {
      return key.startsWith('data-');
    })
  );

  return (
    <LinkComponent
      aria-current={ariaCurrent}
      aria-label={ariaLabel}
      className={props.className ? props.className : styles.link}
      data-color={props.className ? undefined : colorMaybeNull}
      data-weight={props.className ? undefined : weight}
      data-zds
      href={trimmedHref}
      id={props.id}
      name={props.name}
      onBlur={props.onBlur}
      onClick={props.onClick}
      onFocus={props.onFocus}
      onKeyDown={props.onKeyDown}
      onMouseDown={props.onMouseDown}
      onMouseEnter={props.onMouseEnter}
      onMouseLeave={props.onMouseLeave}
      onMouseUp={props.onMouseUp}
      ref={ref}
      rel={buildRel(props.rel, isLinkExternal(props.href), noReferrer)}
      role={props.role}
      style={props.style}
      tabIndex={props.tabIndex}
      target={props.target}
      referrerPolicy={props.referrerPolicy}
      title={props.title}
      {...dataAttributes}
    >
      {props.children}
    </LinkComponent>
  );
});
